import type { GetStatisticResponseDto } from '@pickthebank/api-sdk/type/response/deposit/get-statistic-response.dto';
import { marked } from 'marked';
import { useTranslation } from 'next-i18next';
import Link from 'next-translate-routes/link';
import { useRouter } from 'next-translate-routes/router';
import React, { type FC, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import {
  AdvertisementBankCard,
  Container,
  StatisticsSearchBar,
} from '@/components';
import { ActionBlock } from '@/components/ActionBlock';
import { HeroV2 } from '@/components/HeroV2';
import { LazyLoaded } from '@/components/LazyLoaded';
import { DescriptionOfModalStatistics } from '@/components/SearchBar/parts';
import { StatisticsTable } from '@/components/StatisticsTable/StatisticsTable';
import { useCmsContent } from '@/contexts/CmsContentContext';
import { useDepositsLayout } from '@/layouts/DepositsLayout/useDepositsLayout';
import BlockWithTitle from '@/layouts/FixedGermanDeposits/parts/BlockWithTitle';
import europePng from '@/public/assets/pics/europe@2x-min.png';
import { getBankLogoURL } from '@/services/supabase';
import { AnalyticsEvent } from '@/types/analytics';
import { cn } from '@/utils';

export interface StatisticsRatesLayoutProps {
  countries: GetStatisticResponseDto[];
  className?: string;
  fetchCountries: () => Promise<void>;
  productType: 'savings account' | 'fixed term deposit';
  sections?: any[];
}

export const StatisticsRatesLayout: FC<StatisticsRatesLayoutProps> = ({
  className,
  countries,
  fetchCountries,
  productType,
  sections,
}) => {
  const { t } = useTranslation();
  const { seoContent } = useCmsContent();
  const router = useRouter();

  const isMobile = useMediaQuery({
    query: '(max-width: 728px)',
  });

  const { syncUrlWithStore, updateUrlWithQuery } = useDepositsLayout();

  useEffect(() => {
    syncUrlWithStore();
  }, []);
  const handleOnSubmit = () => {
    fetchCountries();
    if (productType === 'savings account') {
      updateUrlWithQuery('/statistics/savings-accounts', ['country', 'terms']);
    }
    if (productType === 'fixed term deposit') {
      updateUrlWithQuery(
        '/statistics/maximum-interest-rates-in-different-countries',
        ['country'],
      );
    }
  };

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-lg overflow-hidden',
        className,
      )}
    >
      <div className="-z-10 w-full bg-blue-100 lg:min-h-[unset]">
        <HeroV2
          h1TranslationKey={
            productType === 'savings account'
              ? 'savings-accounts.default.h1'
              : 'statistics.title'
          }
        />
      </div>

      <Container className="max-w-[848px] p-5 lg:p-0">
        <div className="md:shadow-far z-10 mx-auto -mt-12 w-full shrink-0 rounded-2xl py-6 md:max-w-none md:bg-white md:p-6">
          <StatisticsSearchBar
            isScrolling={false}
            onSubmit={handleOnSubmit}
            productType={productType}
          />
          <div className="shadow-far z-10 mx-auto -mt-12 flex w-full flex-col gap-2 rounded-2xl bg-white py-6 md:hidden">
            {isMobile && (
              <LazyLoaded>
                <DescriptionOfModalStatistics
                  buttonChange
                  onSubmit={handleOnSubmit}
                  productType={productType}
                />
              </LazyLoaded>
            )}
          </div>
        </div>
      </Container>

      <Container className="px-6 lg:pt-6">
        <AdvertisementBankCard
          bankName="Lidion Bank"
          annualFee={t('savings-accounts.annual-fee.free')}
          rate={t('ad.lidion.rate')}
          image={getBankLogoURL('bank3530MT')}
          comment={t('ad.lidion.comment')}
          brokerUrl="/lidion"
          cashback={null}
          className="mb-3 mt-4 w-full"
          country="Malta"
          primaryColor="#326BFF"
          directUrl="/lidion"
          blockViewEvent={AnalyticsEvent.lidionBlockView}
          openOnlineEvent={AnalyticsEvent.lidionClickOpenOnline}
          shareEvent={AnalyticsEvent.lidionClickShare}
          protectedUpToValue="€100K"
        />
      </Container>

      <Container className="max-w-[848px] px-6 lg:p-6">
        <h5>
          {productType === 'savings account'
            ? t('savings-accounts.default.heading')
            : t('statistics.description')}
        </h5>
        <StatisticsTable
          productType={productType}
          countries={countries}
          className="mt-4"
        />
      </Container>
      {sections && !seoContent?.mainText && (
        <div className="seo-content">
          <Container className="mt-12 max-w-[850px] px-5 lg:px-0">
            <BlockWithTitle
              title={t('sections.statistics.heading')}
              className="mb-12 pt-4"
              titleClassName="text-[32px] font-bold mb-7 tracking-[0.8px]"
            >
              <ul>
                {sections.map((section) => (
                  <li key={section.title}>
                    <Link
                      href={`${router.asPath.split('#')[0]}#${section.title}`}
                    >
                      {section.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </BlockWithTitle>
            {sections.map((section) => (
              <BlockWithTitle
                key={section.title}
                title={section.title}
                className="mb-16 last:mb-20"
              >
                {section.content}
              </BlockWithTitle>
            ))}
          </Container>
        </div>
      )}
      {seoContent?.mainText && (
        <Container className="my-12 max-w-[850px] px-5 lg:px-0">
          <div
            dangerouslySetInnerHTML={{
              __html: marked(seoContent.mainText),
            }}
            className="markdown-article flex flex-col gap-4 md:mx-[34px] lg:mx-auto lg:flex lg:w-full lg:max-w-[849px] lg:flex-col"
          />
        </Container>
      )}
      <ActionBlock
        title={t('statistics.action_title')}
        image={europePng.src}
        action={
          productType === 'savings account'
            ? { title: t('global.all_products'), url: '/savings-accounts' }
            : { title: t('global.all_deposits'), url: '/fixed-deposits' }
        }
        className="mt-12 lg:mt-6"
      />
    </div>
  );
};
